import React, { ChangeEvent, useCallback, useState } from 'react';

import { Box, Button, Stack, TextField } from '@mui/material';

import InputCruiseLineSelect from '~/components/Cruises/components/Selects/InputCruiseLineSelect';
import InputRateCodeSelect from '~/components/Cruises/components/Selects/InputRateCodeSelect';
import { DealPageFilters } from '~/components/Cruises/context/useCruiseDealsPage';

import { CruiseLine } from '~/services/cruises/CruiseLineService';

type Props = {
  isLoading: boolean;
  filters: DealPageFilters;
  onSearch: (filters: DealPageFilters) => void;
};

export default function DealPageSearchForm({ isLoading, filters, onSearch }: Props) {
  const [localFilters, setLocalFilters] = useState<DealPageFilters>(filters);

  const handleChangeField = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    if (event?.target?.name) {
      setLocalFilters((prev) => ({
        ...prev,
        [event.target.name]: event.target.value,
      }));
    }
  }, []);

  const handleCruiseLineField = useCallback((cruiseLine: CruiseLine) => {
    setLocalFilters((prev) => ({
      ...prev,
      cruiseLineId: cruiseLine?.id,
    }));
  }, []);

  const handleRateCodesField = useCallback((rateCodes: Array<string>) => {
    setLocalFilters((prev) => ({
      ...prev,
      rateCodes,
    }));
  }, []);

  const handleSearch = useCallback(() => {
    onSearch({
      name: localFilters.name || undefined,
      cruiseLineId: localFilters.cruiseLineId || undefined,
      rateCodes: localFilters.rateCodes?.length ? localFilters.rateCodes : undefined,
    });
  }, [localFilters, onSearch]);

  return (
    <Stack direction="column" spacing={2} border={1} borderColor="divider" borderRadius={1} p={2}>
      <Stack direction="row" spacing={2}>
        <Box flexGrow={2}>
          <TextField
            fullWidth
            name="name"
            label="Deal Name"
            onChange={handleChangeField}
            value={localFilters.name || ''}
          />
        </Box>
        <Box flexGrow={2}>
          <InputCruiseLineSelect
            label="Cruise line"
            value={localFilters.cruiseLineId}
            onChange={handleCruiseLineField}
            allowEmpty
            withApprovedOffers
          />
        </Box>
      </Stack>
      <Stack direction="row" spacing={2}>
        <Box flexGrow={2}>
          <InputRateCodeSelect
            selectedRateIds={localFilters.rateCodes || []}
            onChange={handleRateCodesField}
            allowEmptyVendorCode
            shouldReturnRateCodes
          />
        </Box>
        <Box>
          <Button variant="contained" disabled={isLoading} onClick={handleSearch}>
            Search Content
          </Button>
        </Box>
      </Stack>
    </Stack>
  );
}
